import React, { Component, ReactNode } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // console.error('Error caught by ErrorBoundary:', error, errorInfo);

    const message = String(error?.message || error);
    const isChunkError =
      message.includes('Failed to fetch dynamically imported module') ||
      message.includes('Importing a module script failed') ||
      message.includes('ChunkLoadError') ||
      message.includes('Loading chunk');

    const hasReloaded = sessionStorage.getItem('chunk-reload') === '1';

    if (isChunkError && !hasReloaded) {
      try {
        sessionStorage.setItem('chunk-reload', '1');
        const url = new URL(window.location.href);
        url.searchParams.set('v', Date.now().toString());
        window.location.replace(url.toString());
      } catch (_) {
        window.location.reload();
      }
    }
  }

  handleReload = () => {
    // Clear the guard so user-triggered reload works again
    sessionStorage.removeItem('chunk-reload');
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <Card className="bg-red-50 border-red-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-900">
              <AlertTriangle className="h-5 w-5" />
              Jokin meni pieleen
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-red-800 mb-4">
              Sivulla tapahtui odottamaton virhe. Yritä päivittää sivu tai ota yhteyttä tukeen, jos ongelma jatkuu.
            </p>
            <div className="flex gap-3">
              <Button onClick={this.handleReload} variant="outline" className="flex items-center gap-2">
                <RefreshCw className="h-4 w-4" />
                Päivitä sivu
              </Button>
            </div>
            {this.state.error && (
              <details className="mt-4 p-3 bg-white rounded border">
                <summary className="cursor-pointer text-sm font-medium text-red-700">
                  Näytä tekninen virheviesti
                </summary>
                <pre className="mt-2 text-xs text-red-600 overflow-auto">
                  {this.state.error.toString()}
                </pre>
                {String(this.state.error?.message || '')?.includes('Failed to fetch dynamically imported module') && (
                  <p className="mt-3 text-xs text-red-700">
                    Havaittiin versiopäivitys. Päivitämme sivun automaattisesti latausongelman korjaamiseksi.
                  </p>
                )}
              </details>
            )}
          </CardContent>
        </Card>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;